import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import styles from './MyArticles.module.css';
import logger from '../../../utils/logger';
const MyArticles = () => {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [myStats, setMyStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    

    useEffect(() => {
        loadData();
    }, [currentPage]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [articlesResponse, statsResponse] = await Promise.all([
                AdminService.getMyArticles(currentPage, 20),
                AdminService.getMyArticlesStats()
            ]);
            
            setArticles(articlesResponse.data || []);
            setTotalPages(articlesResponse.pages || 1);
            setMyStats(statsResponse);
        } catch (err) {
            setError('Ошибка загрузки данных');
            logger.error('Ошибка загрузки моих статей:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (articleId) => {
        navigate(`/main/articles/edit/${articleId}`);
    };

    const handleDelete = async (articleId, articleTitle) => {
        if (!window.confirm(`Вы уверены, что хотите удалить статью "${articleTitle}"?`)) {
            return;
        }

        try {
            await AdminService.deleteArticle(articleId);
            await loadData(); // Перезагружаем список
        } catch (error) {
            logger.error('Ошибка удаления статьи:', error);
            alert('Ошибка удаления статьи');
        }
    };

    const handleView = (articleId) => {
        navigate(`/main/articles/${articleId}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Загрузка ваших статей...</p>
            </div>
        );
    }

    return (
        <div className={styles.myArticlesContainer}>
            {/* Заголовок */}
            <div className={styles.header}>
                <button 
                    onClick={() => navigate('/main/articles')}
                    className={styles.backButton}
                >
                    ← Назад к статьям
                </button>
                <h1>Мои статьи</h1>
                <button 
                    onClick={() => navigate('/main/articles/create')}
                    className={styles.createButton}
                >
                    ✏️ Написать статью
                </button>
            </div>

            <div className={styles.contentWrapper}>
                {/* Основной контент */}
                <div className={styles.mainContent}>
                    {error ? (
                        <div className={styles.errorContainer}>
                            <p>{error}</p>
                            <button onClick={loadData} className={styles.retryButton}>
                                Попробовать снова
                            </button>
                        </div>
                    ) : articles.length === 0 ? (
                        <div className={styles.emptyState}>
                            <h3>У вас пока нет статей</h3>
                            <p>Создайте свою первую статью, чтобы поделиться знаниями с сообществом</p>
                            <button 
                                onClick={() => navigate('/main/articles/create')}
                                className={styles.createFirstArticle}
                            >
                                Написать первую статью
                            </button>
                        </div>
                    ) : (
                        <div className={styles.articlesList}>
                            {articles.map(article => (
                                <MyArticleCard 
                                    key={article.id} 
                                    article={article}
                                    onEdit={() => handleEdit(article.id)}
                                    onDelete={() => handleDelete(article.id, article.title)}
                                    onView={() => handleView(article.id)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Пагинация */}
                    {totalPages > 1 && (
                        <div className={styles.pagination}>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`${styles.pageButton} ${
                                        page === currentPage ? styles.activePage : ''
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Боковая панель со статистикой */}
                <div className={styles.sidebar}>
                    <MyStatsPanel stats={myStats} />
                </div>
            </div>
        </div>
    );
};

// Компонент карточки моей статьи
const MyArticleCard = ({ article, onEdit, onDelete, onView }) => {
    return (
        <div className={styles.articleCard}>
            <div className={styles.cardHeader}>
                <div className={styles.titleSection}>
                    <h3 className={styles.articleTitle} onClick={onView}>
                        {article.title}
                    </h3>
                    <div className={styles.articleMeta}>
                        <span className={styles.status}>
                            {article.is_published ? '🟢 Опубликовано' : '🔴 Черновик'}
                        </span>
                        <span className={styles.date}>
                            📅 {AdminService.formatDate(article.created_at)}
                        </span>
                        {article.updated_at !== article.created_at && (
                            <span className={styles.updatedDate}>
                                ✏️ Обновлено: {AdminService.formatDate(article.updated_at)}
                            </span>
                        )}
                        <span className={styles.difficulty}>
                            🎯 {AdminService.getDifficultyText(article.difficulty_level)}
                        </span>
                    </div>
                </div>
                
                <div className={styles.actions}>
                    <button 
                        onClick={onView}
                        className={styles.viewButton}
                        title="Просмотреть статью"
                    >
                        👁️ Просмотр
                    </button>
                    <button 
                        onClick={onEdit}
                        className={styles.editButton}
                        title="Редактировать статью"
                    >
                        ✏️ Редактировать
                    </button>
                    <button 
                        onClick={onDelete}
                        className={styles.deleteButton}
                        title="Удалить статью"
                    >
                        🗑️ Удалить
                    </button>
                </div>
            </div>

            {/* Краткое описание */}
            {article.summary && (
                <p className={styles.articleSummary}>{article.summary}</p>
            )}

            {/* Теги */}
            {article.tags && article.tags.length > 0 && (
                <div className={styles.articleTags}>
                    {article.tags.slice(0, 5).map(tag => (
                        <span key={tag.id} className={styles.tag}>
                            #{tag.name}
                        </span>
                    ))}
                    {article.tags.length > 5 && (
                        <span className={styles.moreTags}>+{article.tags.length - 5}</span>
                    )}
                </div>
            )}

            {/* Статистика статьи */}
            <div className={styles.articleStats}>
                <span className={styles.views}>
                    👁️ {AdminService.formatLargeNumber(article.views_count)} просмотров
                </span>
                <span className={styles.rating}>
                    ⭐ {AdminService.formatRating(article.rating)} ({article.rating_count} оценок)
                </span>
                <span className={styles.comments}>
                    💬 {article.comments_count} комментариев
                </span>
                <span className={styles.readTime}>
                    ⏱️ {AdminService.formatReadTime(article.estimated_read_time)}
                </span>
            </div>
        </div>
    );
};

// Компонент панели со статистикой
const MyStatsPanel = ({ stats }) => {
    return (
        <div className={styles.statsPanel}>
            <h3>📊 Моя статистика</h3>
            
            {/* Основная статистика */}
            <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                    <div className={styles.statValue}>{stats.total_articles || 0}</div>
                    <div className={styles.statLabel}>Статей написано</div>
                </div>
                
                <div className={styles.statItem}>
                    <div className={styles.statValue}>⭐ {AdminService.formatRating(stats.avg_rating)}</div>
                    <div className={styles.statLabel}>Средний рейтинг</div>
                </div>
                
                <div className={styles.statItem}>
                    <div className={styles.statValue}>{AdminService.formatLargeNumber(stats.total_views)}</div>
                    <div className={styles.statLabel}>Всего просмотров</div>
                </div>
                
                <div className={styles.statItem}>
                    <div className={styles.statValue}>{stats.total_comments || 0}</div>
                    <div className={styles.statLabel}>Комментариев получено</div>
                </div>
            </div>

            {/* Рейтинг автора */}
            {stats.author_rank && (
                <div className={styles.authorRank}>
                    <h4>🏆 Место в рейтинге авторов</h4>
                    <div className={styles.rankValue}>#{stats.author_rank}</div>
                </div>
            )}

            {/* Мои теги */}
            {stats.my_tags && stats.my_tags.length > 0 && (
                <div className={styles.myTags}>
                    <h4>🏷️ Мои теги</h4>
                    <div className={styles.tagsList}>
                        {stats.my_tags.slice(0, 10).map((tag, index) => (
                            <div key={index} className={styles.tagItem}>
                                <span className={styles.tagName}>#{tag.name}</span>
                                <span className={styles.tagCount}>{tag.usage_count}</span>
                            </div>
                        ))}
                    </div>
                    {stats.my_tags.length > 10 && (
                        <p className={styles.moreTags}>
                            И ещё {stats.my_tags.length - 10} тегов...
                        </p>
                    )}
                </div>
            )}

            {/* Советы */}
            <div className={styles.tipsSection}>
                <h4>💡 Советы</h4>
                <ul className={styles.tipsList}>
                    <li>Используйте качественные изображения в статьях</li>
                    <li>Добавляйте релевантные теги для лучшего поиска</li>
                    <li>Отвечайте на комментарии читателей</li>
                    <li>Регулярно обновляйте старые статьи</li>
                </ul>
            </div>
        </div>
    );
};

export default MyArticles;